import type { Assets } from '@design-system/components/UI/UIProductTile.props'
import type { PlpPageProps } from '@integration-layer/components/Templates/TemplatesPlp.props'

/**
 * Creates a reactive state for storing Product Listing Page (PLP) data
 *
 * @returns {Ref<PlpPageProps | null>} Reactive state for PLP data
 */
export const useTemplatePlpState = () =>
  useState<PlpPageProps | null>('template_plp_state', () => null)

/**
 * Composable for handling Product Listing Page (PLP) functionality
 *
 * @returns {Object} An object containing methods for PLP data management and UI interactions
 */
export const useTemplatePlp = async () => {
  /**
   * List of filters used for refining product search
   * @type {string[]}
   */
  const FILTERS = [
    'Gender',
    'macroCat',
    'microCat',
    'MacroColorDesc',
    'ColorDesc',
    'size.sizeCode',
    'attributes.Occasion',
    'attributes.Online',
    'highlighted',
  ]

  const nuxtApp = useNuxtApp()
  const { currentWebsite } = useWebsite()

  const { getShotRotation } = useBrandsSettings()

  /**
   * Determines the order of product shots based on current website
   * @type {number[]}
   */
  const orderCms = getShotRotation(currentWebsite.value)
    ? getShotRotation(currentWebsite.value)
    : [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]

  /**
   * Cookie for storing grid layout preference
   * @type {Ref<{ name: string }>}
   */
  const gridLayout = useCookie<{ name: string }>('grid-layout-cookies')

  /**
   * Reactive state for determining grid display type (mixed or still)
   * @type {Ref<string>}
   */
  const mixedOrStillGrid = ref(
    gridLayout?.value?.name === 'gridMoodboard' ||
      gridLayout?.value?.name === 'gridWall'
      ? 'still'
      : 'mixed'
  )

  /**
   * Watches grid layout changes and updates mixedOrStillGrid accordingly
   */
  watch(gridLayout, () => {
    if (
      gridLayout?.value?.name === 'gridMoodboard' ||
      gridLayout?.value?.name === 'gridWall'
    ) {
      mixedOrStillGrid.value = 'still'
    } else {
      mixedOrStillGrid.value = 'mixed'
    }
  })

  /**
   * Mapping of media positions for different grid types
   * @type {Array<{type: string, grids: Array<{order: number[]}>}>}
   */
  const mediaPositionMap = [
    {
      type: 'mixed',
      grids: [
        {
          order: orderCms!,
        },
      ],
    },
    {
      type: 'still',
      grids: [
        {
          order: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
        },
      ],
    },
  ]

  const { width } = useWindowSize()

  /**
   * Transforms subcategory items into a grouped refinement structure
   *
   * @param {RefinementListItem[]} items - List of refinement items
   * @returns {RefinementGroup[]} Grouped refinement items
   */
  const transformSubCategory = (
    items: RefinementListItem[]
  ): RefinementGroup[] => {
    const grouped: Record<string, RefinementGroup> = {}

    items.forEach(item => {
      const [category, label] = item.value.split(' > ')

      if (!grouped[category]) {
        grouped[category] = {
          title: category,
          refinementsList: [],
        }
      }

      grouped[category].refinementsList.push({
        ...item,
        label: label,
      })
    })

    return Object.values(grouped) as RefinementGroup[]
  }

  /**
   * Retrieves media assets by a specific position
   *
   * @param {Assets[]} assets - List of assets
   * @param {number} position - Desired position
   * @returns {Assets[]} Reordered assets
   */
  const getMediaByPosition = (assets: Assets[], position: number): Assets[] => {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return []
    }
    // order assets by priority
    const assetsBypriority = assets.sort(
      ({ priority: a }, { priority: b }) => a! - b!
    )
    position = Math.max(1, Math.min(position, assets.length))

    const adjustedPosition = position - 1
    // reorder assets based on the position
    return [
      assetsBypriority[adjustedPosition],
      ...assetsBypriority.slice(0, adjustedPosition),
      ...assetsBypriority.slice(adjustedPosition + 1),
    ]
  }

  /**
   * Calculates the item's position within a page
   *
   * @param {number} itemIndex - Index of the item
   * @param {number} itemsPerPage - Number of items per page
   * @returns {number} Calculated position
   */
  const calculateItemPosition = (
    itemIndex: number,
    itemsPerPage: number
  ): number => {
    return itemIndex % itemsPerPage || itemsPerPage
  }

  /**
   * Gets the media position for an item
   *
   * @param {number} index - Item index
   * @param {string} gridType - Type of grid (mixed or still)
   * @param {number} hits - Total number of items
   * @returns {number | undefined} Media position
   */
  const getPosition = (index: number, gridType: string, hits: number) => {
    const currentPositionInPage = calculateItemPosition(index + 1, hits)
    const currentIndex = currentPositionInPage - 1

    const position = mediaPositionMap.find(media => media.type === gridType)
      ?.grids[0].order[currentIndex]
    return position
  }

  /**
   * Rearranges image positions based on grid type and item index
   *
   * @param {Assets[]} images - List of image assets
   * @param {number} itemIndex - Index of the item
   * @param {number} totalItems - Total number of items
   * @returns {Assets[]} Rearranged images
   */
  const rearrangeImagePosition = (
    images: Assets[],
    itemIndex: number,
    totalItems: number
  ) => {
    return getMediaByPosition(
      images,
      getPosition(itemIndex, mixedOrStillGrid.value, totalItems) || 1
    )
  }

  /**
   * Counts active refinements for a specific attribute
   *
   * @param {CurrentRefinements[]} items - List of current refinements
   * @param {string} attribute - Attribute to check
   * @returns {number} Number of active refinements
   */
  const getActiveRefinementLength = (
    items: CurrentRefinements[],
    attribute: string
  ) => {
    const activeRefinementLenght = items.filter(
      item => item.attribute === attribute
    )?.length

    return activeRefinementLenght
  }

  /**
   * Calculates total number of active refinements across all filters
   *
   * @param {CurrentRefinements[]} items - List of current refinements
   * @returns {number} Total number of active refinements
   */
  const getAllActiveRefinementLength = (items: CurrentRefinements[]) => {
    let totalActiveRefinementLength = 0
    for (const filter of FILTERS) {
      totalActiveRefinementLength +=
        getActiveRefinementLength(items, filter) || 0
    }
    return totalActiveRefinementLength
  }

  /**
   * Transforms refinements into a more manageable format
   *
   * @param {CurrentRefinements[]} items - List of current refinements
   * @returns {CurrentRefinements[]} Transformed refinements
   */
  const transformRefinements = (items: CurrentRefinements[]) => {
    const transformedRefinements = <CurrentRefinements[]>[]

    items.forEach(item => {
      item.refinements.forEach(refinement => {
        transformedRefinements.push({
          indexName: item.indexName,
          indexId: item.indexId,
          attribute: item.attribute,
          label: item.label,
          refine: item.refine,
          refinements: [refinement],
        })
      })
    })
    return transformedRefinements
  }

  /**
   * Determines whether a filter should be hidden
   *
   * @param {HTMLElement | null} mainDiv - Main container element
   * @returns {boolean} Whether the filter should be hidden
   */
  const hideFilter = (mainDiv: HTMLElement | null) => {
    if (!mainDiv) return false
    const noRefinementElement = mainDiv.querySelector(
      '.ais-RefinementList--noRefinement'
    )
    const oneFacetElement = mainDiv.querySelector('.oneFacetElement')

    return noRefinementElement !== null || oneFacetElement !== null
  }

  return {
    width,
    currentWebsite,
    mixedOrStillGrid,
    rearrangeImagePosition,
    transformSubCategory,
    transformRefinements,
    getActiveRefinementLength,
    getAllActiveRefinementLength,
    hideFilter,
  }
}
